import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { connect } from "react-redux";
import CustomBarChart from "./CustomChart";

// icn
import { ReactComponent as DownloadIcn } from "Assets/Images/icn/download.svg";

import { getDataAnalysisGraphsV2 } from "store/services/clientDataReportsService";
import { FULL_NAME } from "helpers/common";
import * as actions from "store/actions";
import DownloadComparisionGraph from "./ComparisionGraphPDF";
import SelectNAQs from "components/shared/ReportComponents/SelectNAQs";
import { MAX_SELECT_GRAPH_COMPARISION } from "common/constants";
import { LineChart } from "./LineChart";
import { getChartHeightByScreenSize, getScreenSize } from "components/shared/ChartConfig";
import { cloneDeep } from "lodash";

const Naqv2SymptomGraph = ({ dispatch, naqList, client }) => {
  const { clientId } = useParams();
  const [ foundationsData, setFoundationsData ] = useState(null);
  const [ bodySystemsData, setBodySystemsData ] = useState(null);
  const [ selectedNAQs, setSelectedNAQs ] = useState([]);
  const [ selectedChartType, setSelectedChartType ] = useState("bar");
  const [chartHeight, setChartHeight] = useState(150);
  const [screenSize, setScreenSize] = useState("extra-large");

  // FSB Graph
  const fsbBarGraphRef = useRef(null);
  const fsbLineGraphRef = useRef(null);

  // BS Graph
  const bsBarGraphRef = useRef(null);
  const bsLineGraphRef = useRef(null);

  useEffect(() => {
    let size = getScreenSize(screenSize);
    setScreenSize(size);
  }, []);

  useEffect(() => {
    let height = getChartHeightByScreenSize(screenSize, chartHeight);
    setChartHeight(height);
  }, [screenSize]);

  useEffect(() => {
    if(selectedNAQs && selectedNAQs.length > 1) {
      getNAQv2GraphData(selectedNAQs);
    }
  }, [selectedNAQs]);

  const fsbLineChartData = useMemo(() => {
    if(!foundationsData?.length) {
      return null;
    }
    return cloneDeep(foundationsData).map((item) => {
      if(item?.data?.length) {
        item.data = item.data.filter(row => row.isChild === 0);
      }
      return item;
    })
  }, [foundationsData]);

  const getNAQv2GraphData = async (todoIds) => {
    try {
      let data = {
        clientId,
        values: {
          todoIds
        }
      } 
      dispatch(actions.persist_store({ loader: true }));
      const response = await getDataAnalysisGraphsV2(data);
      dispatch(actions.persist_store({ loader: false }));
      if( response.success === true &&
        ( response.body?.body_systems?.length && response.body?.foundations?.length )
      ) {
        const { foundations, body_systems: bodySystems } = response.body;
        setFoundationsData(foundations);
        setBodySystemsData(bodySystems);
      }
    } catch(error) {
      console.log("Error getting graph data", error);
      dispatch(actions.persist_store({ loader: false }));
    }
  }

  const handleChangeSelectedNAQ = (selected) => {
    console.log("selected", selected)
    setSelectedNAQs(selected);
  }

  const getNAQColor = (naqId) => {
    let naq = naqList.find(naq => naq.id === naqId);
    return naq && naq.color;
  }
  
  return (
    <>
      <div className="top d-flex align-items-start justify-content-between gap-10">
        <div className="left">
          <h6 className="m-0 fw-bold">Symptom Burden Comparison Graph</h6>
          <p className="m-0">
            Compare Symptom Burden Graphs from {FULL_NAME(client.first_name, client.last_name)}'s NAQs.
          </p>
        </div>
        <div className="right">
          <DownloadComparisionGraph
            client={client}
            selectedNAQs={naqList && naqList.filter(naq => selectedNAQs.includes(naq.id))}
            chartRef={selectedChartType === "bar" ? fsbBarGraphRef : fsbLineGraphRef}
            pdfLabel={"Symptom Burden Comparison Graph"}
          />
        </div>
      </div>
      <div className="CardBody p-3 bg-light my-2">
        <div className="top d-flex align-items-center justify-content-between gap-10">
          <div className="left">
            <SelectNAQs
              naqList={naqList}
              onChange={handleChangeSelectedNAQ}
              maxSelectable={MAX_SELECT_GRAPH_COMPARISION}
            />
          </div>
          <div className="right">
            <Form.Label>
              Chart type:
            </Form.Label>
            <Form.Select
              onChange={(e) => {
                setSelectedChartType(e.target.value);
              }}
              value={selectedChartType}
            >
              <option value="bar">Bar Chart</option>
              <option value="line">Line Chart</option>
            </Form.Select>
          </div>
        </div>
        <div className="GraphWrpper my-2 py-2">
          <div className="foundation-comparision">
            <div className="top d-flex align-items-start justify-content-between gap-10">
              <div className="left">
                <h6 className="m-0 fw-bold">Foundations Comparison</h6>
              </div>
              <div className="right">
                {/* <Button
                  className="d-flex align-items-center justify-content-center commonBtn btnSm"
                >
                  <span className="icn me-2">
                    <DownloadIcn />
                  </span>
                  Download Report
                </Button> */}
              </div>
            </div>
            {selectedChartType == "bar" ? (
              <CustomBarChart
                innerRef={fsbBarGraphRef}
                dataset={foundationsData}
                naqList={naqList}
                selectedNAQs={selectedNAQs}
              />
            ):(
              <LineChart
                innerRef={fsbLineGraphRef}
                chartData={fsbLineChartData}
                naqList={naqList}
                selectedNAQs={selectedNAQs}
                getColor={getNAQColor}
                height={chartHeight}
              />
            )}
          </div>
          <div className="body-systems-comparision py-3">
            <div className="top d-flex align-items-start justify-content-between gap-10">
              <div className="left">
                <h6 className="m-0 fw-bold">Body Systems Comparison</h6>
              </div>
              <div className="right">
                <DownloadComparisionGraph
                  client={client}
                  selectedNAQs={naqList && naqList.filter(naq => selectedNAQs.includes(naq.id))}
                  chartRef={selectedChartType === "bar" ? bsBarGraphRef : bsLineGraphRef}
                  pdfLabel={"Body Systems Comparison"}
                />
              </div>
            </div>
            {selectedChartType == "bar" ? (
              <CustomBarChart
                innerRef={bsBarGraphRef}
                dataset={bodySystemsData}
                naqList={naqList}
                selectedNAQs={selectedNAQs}
              />
            ):(
              <LineChart
                innerRef={bsLineGraphRef}
                chartData={bodySystemsData}
                naqList={naqList}
                selectedNAQs={selectedNAQs}
                getColor={getNAQColor}
                height={chartHeight}
              />
            )}
          </div>
        </div>
        <p className="m-0 py-2 text-dark">
          *Looking for all the NAQ questions/answers your client submitted? All
          of the NAQ data is under the{" "}
          <Link to="" className="underline">
            Data & To-Do Items
          </Link>{" "}
          tab.
        </p>
      </div>
    </>
  );
};

const mapStateToPros = (state) => {
  return {
    client: state.Client.client.details
  };
}

function mapDispatchToProps(dispatch) {
  return { dispatch };
}

export default connect(mapStateToPros, mapDispatchToProps)(Naqv2SymptomGraph);